// src/services/pipsCreatureKeeperHabitatService.ts
import { logger } from '../utils/logger';

// FIXED: Proper happiness calculation using type1/type2/compatibility columns from database



import { Creature, TypeCompatibility, HabitatPlacement, GameData, CompatibilityInfo } from '../types/pipsCreatureKeeper';

import { updateCreatureHappiness } from './pipsCreatureKeeperService';



/**

 * Calculate compatibility between two creatures based on their types

 * FIXED: Now properly uses type1/type2/compatibility column names from database

 */

export function calculateCompatibility(

  creature1: Creature,

  creature2: Creature,

  typeCompatibility: TypeCompatibility[]

): number {

  logger.log(`\n Calculating compatibility between ${creature1.name} and ${creature2.name}`);



  const types1 = [creature1.type1, creature1.type2].filter(Boolean);

  const types2 = [creature2.type1, creature2.type2].filter(Boolean);



  logger.log(`  ${creature1.name} types:`, types1);

  logger.log(`  ${creature2.name} types:`, types2);



  let totalMultiplier = 0;

  let comparisons = 0;



  for (const type1 of types1) {

    for (const type2 of types2) {

      // FIXED: Use type1/type2 column names from database (case-insensitive)

      const compat1 = typeCompatibility.find(tc => {

        const tcType1 = tc.type1;

        const tcType2 = tc.type2;

        if (!tcType1 || !tcType2 || !type1 || !type2) return false;

        return tcType1.toLowerCase() === type1.toLowerCase() &&

               tcType2.toLowerCase() === type2.toLowerCase();

      });

      const compat2 = typeCompatibility.find(tc => {

        const tcType1 = tc.type1;

        const tcType2 = tc.type2;

        if (!tcType1 || !tcType2 || !type1 || !type2) return false;

        return tcType1.toLowerCase() === type2.toLowerCase() &&

               tcType2.toLowerCase() === type1.toLowerCase();

      });



      // FIXED: Use 'compatibility' column name from database

      const mult1 = compat1 ? parseFloat(compat1.compatibility.toString()) : 1.0;

      const mult2 = compat2 ? parseFloat(compat2.compatibility.toString()) : 1.0;

      const avgMult = (mult1 + mult2) / 2;



      logger.log(`    ${type1} vs ${type2}:`);

      logger.log(`      ${type1}  ${type2}: ${mult1}x`);

      logger.log(`      ${type2}  ${type1}: ${mult2}x`);

      logger.log(`      Average: ${avgMult.toFixed(2)}x`);



      totalMultiplier += avgMult;

      comparisons++;

    }

  }



  const finalCompatibility = comparisons > 0 ? totalMultiplier / comparisons : 1.0;

  logger.log(`   Final compatibility: ${finalCompatibility.toFixed(2)}x (${comparisons} comparisons)`);



  return finalCompatibility;

}



/**

 * Calculate a single creature's happiness in a habitat

 * FIXED: Now returns proper happiness based on compatibility with detailed logging

 */

export function calculateCreatureHappiness(

  creatureId: number,

  habitatCreatures: HabitatPlacement[],

  creatures: Creature[],

  typeCompatibility: TypeCompatibility[]

): number {

  const targetCreature = creatures.find(c => c.id === creatureId);



  if (!targetCreature) {

    logger.warn(' Target creature not found:', creatureId);

    return 70;

  }



  logger.log(`\n Calculating happiness for ${targetCreature.name} (ID: ${creatureId})`);



  if (habitatCreatures.length <= 1) {

    logger.log(`  Living alone - base happiness: 70%`);

    return 70; // Base happiness for solo creatures

  }



  let totalCompatibility = 0;

  let companionCount = 0;



  habitatCreatures.forEach(habitatCreature => {

    if (habitatCreature.creature_id !== creatureId) {

      const companionCreature = creatures.find(c => c.id === habitatCreature.creature_id);

      if (companionCreature) {

        logger.log(`   Checking compatibility with ${companionCreature.name}`);

        const compatibility = calculateCompatibility(targetCreature, companionCreature, typeCompatibility);

        totalCompatibility += compatibility;

        companionCount++;

      }

    }

  });



  if (companionCount === 0) {

    logger.log(`  No companions found - base happiness: 70%`);

    return 70;

  }



  // Convert compatibility to happiness

  // 0.5x compatibility = 40% happiness (unhappy)

  // 1.0x compatibility = 70% happiness (neutral)

  // 1.5x compatibility = 85% happiness (happy)

  // 2.0x compatibility = 100% happiness (very happy)

  const avgCompatibility = totalCompatibility / companionCount;

  logger.log(`   Average compatibility: ${avgCompatibility.toFixed(2)}x (${companionCount} companions)`);



  // Formula: happiness = 25 + (avgCompatibility * 37.5)

  // This gives us: 0.5x -> 43.75%, 1.0x -> 62.5%, 1.5x -> 81.25%, 2.0x -> 100%

  let happiness = Math.round(25 + (avgCompatibility * 37.5));



  // Ensure happiness stays within bounds

  happiness = Math.max(10, Math.min(100, happiness));



  logger.log(`   Final happiness: ${happiness}%`);



  return happiness;

}



/**

 * Update happiness for all creatures in a specific habitat

 * OPTIMIZED: Only updates one habitat instead of all

 */

export async function updateHabitatHappiness(

  habitatId: number,

  habitatPlacements: { [key: number]: HabitatPlacement[] },

  creatures: Creature[],

  typeCompatibility: TypeCompatibility[]

): Promise<void> {

  const habitatCreatures = habitatPlacements[habitatId] || [];



  if (habitatCreatures.length === 0) {

    logger.log(` Habitat ${habitatId} is empty, skipping happiness update`);

    return;

  }



  logger.log(`\n Updating happiness for habitat ${habitatId} (${habitatCreatures.length} creatures)`);



  // Check if type compatibility data is loaded

  if (!typeCompatibility || typeCompatibility.length === 0) {

    logger.warn(' Type compatibility data not loaded! Using default happiness values.');

  } else {

    logger.log(` Type compatibility data loaded: ${typeCompatibility.length} entries`);

  }



  // Batch all updates together

  const updates = habitatCreatures.map(habitatCreature => {

    const newHappiness = calculateCreatureHappiness(

      habitatCreature.creature_id,

      habitatCreatures,

      creatures,

      typeCompatibility

    );



    // Update local data immediately

    habitatCreature.happiness = newHappiness;



    const creatureName = creatures.find(c => c.id === habitatCreature.creature_id)?.name || 'Unknown';

    logger.log(`  - ${creatureName}: ${newHappiness}%`);



    return { id: habitatCreature.id, happiness: newHappiness };

  });



  // Execute all database updates in parallel

  await Promise.all(

    updates.map(update => updateCreatureHappiness(update.id, update.happiness))

  );



  logger.log(` Habitat ${habitatId} happiness updated`);

}



/**

 * Update happiness for multiple habitats efficiently

 * OPTIMIZED: Batches all updates

 */

export async function updateMultipleHabitatHappiness(

  habitatIds: number[],

  habitatPlacements: { [key: number]: HabitatPlacement[] },

  creatures: Creature[],

  typeCompatibility: TypeCompatibility[]

): Promise<void> {

  await Promise.all(

    habitatIds.map(habitatId =>

      updateHabitatHappiness(habitatId, habitatPlacements, creatures, typeCompatibility)

    )

  );

}



/**

 * Update happiness for all habitats

 * ONLY USE ON INITIAL LOAD - prefer updateHabitatHappiness for single habitat updates

 */

export async function updateAllHappiness(

  habitatPlacements: { [key: number]: HabitatPlacement[] },

  creatures: Creature[],

  typeCompatibility: TypeCompatibility[]

): Promise<void> {

  logger.log('\n Calculating initial happiness for all habitats...');



  // Check if we have the necessary data

  if (!typeCompatibility || typeCompatibility.length === 0) {

    logger.error(' Type compatibility data is missing or empty!');

    logger.log('Cannot calculate happiness without type compatibility data.');

    return;

  }



  logger.log(` Type compatibility loaded: ${typeCompatibility.length} entries`);

  logger.log(` Total habitats: ${Object.keys(habitatPlacements).length}`);



  const habitatIds = Object.keys(habitatPlacements).map(id => parseInt(id));

  await updateMultipleHabitatHappiness(habitatIds, habitatPlacements, creatures, typeCompatibility);



  logger.log(' Initial happiness calculated for all habitats');

}



/**

 * Load habitat placements from player creatures

 */

export function loadHabitatPlacements(

  gameData: GameData

): { [key: number]: HabitatPlacement[] } {

  logger.log('\n  Loading habitat placements...');



  const placements: { [key: number]: HabitatPlacement[] } = {};



  // Initialize all unlocked habitats

  gameData.habitats.forEach(habitat => {

    if (gameData.unlockedHabitats.includes(habitat.id)) {

      placements[habitat.id] = [];

    }

  });



  logger.log(` Initialized ${Object.keys(placements).length} habitats`);



  // Place creatures

  let placedCount = 0;

  gameData.myCreatures.forEach(playerCreature => {

    if (playerCreature.habitat_id && placements[playerCreature.habitat_id]) {

      placements[playerCreature.habitat_id].push({

        id: playerCreature.id,

        creature_id: playerCreature.creature_id,

        happiness: (playerCreature as any).current_happiness || 70

      });

      placedCount++;

    }

  });



  logger.log(` Placed ${placedCount} creatures in habitats`);



  return placements;

}



/**

 * Calculate overall sanctuary happiness

 */

export function calculateOverallHappiness(

  habitatPlacements: { [key: number]: HabitatPlacement[] }

): { overallHappiness: number; unhappyCount: number; totalCreatures: number } {

  let totalCreatures = 0;

  let totalHappiness = 0;

  let unhappyCount = 0;



  Object.values(habitatPlacements).forEach(habitatCreatures => {

    habitatCreatures.forEach(creature => {

      totalCreatures++;

      totalHappiness += creature.happiness;

      if (creature.happiness < 30) unhappyCount++;

    });

  });



  const overallHappiness = totalCreatures > 0 ? totalHappiness / totalCreatures : 100;



  // Add 8% bonus to displayed happiness (capped at 100%)

  const displayHappiness = Math.min(100, Math.round(overallHappiness + 8));



  return {

    overallHappiness: displayHappiness,

    unhappyCount,

    totalCreatures

  };

}



/**

 * Get type compatibility information for display

 * OPTIMIZED: Uses Sets for better performance

 * FIXED: Use type1/type2/compatibility column names from database

 */

export function getCreatureCompatibility(

  types: string[],

  typeCompatibility: TypeCompatibility[]

): CompatibilityInfo {

  const strong = new Set<string>();

  const weak = new Set<string>();



  types.forEach(attackType => {

    typeCompatibility.forEach(compat => {

      // FIXED: Use type1/type2 column names from database

      const type1 = compat.type1;

      const type2 = compat.type2;

      const multiplier = parseFloat(compat.compatibility.toString());



      if (type1?.toLowerCase() === attackType.toLowerCase()) {

        if (multiplier > 1.0) {

          strong.add(type2);

        }

      }

      if (type2?.toLowerCase() === attackType.toLowerCase()) {

        if (multiplier > 1.0) {

          weak.add(type1);

        }

      }

    });

  });



  return {

    strong: Array.from(strong).sort(),

    weak: Array.from(weak).sort()

  };

}



/**

 * Check if player can explore (all creatures placed and happy enough)

 */

export function canExplore(

  myCreatures: any[],

  habitatPlacements: { [key: number]: HabitatPlacement[] },

  creatures: Creature[]

): { allowed: boolean; reason: string } {

  if (myCreatures.length === 0) {

    return { allowed: false, reason: "You need at least one creature to explore territories!" };

  }



  // Check for unplaced creatures

  const placedCreatureIds = new Set<string>();

  Object.values(habitatPlacements).forEach(habitatCreatures => {

    habitatCreatures.forEach(creature => placedCreatureIds.add(creature.id));

  });



  const unplacedCreatures = myCreatures.filter(pc => !placedCreatureIds.has(pc.id));

  if (unplacedCreatures.length > 0) {

    return {

      allowed: false,

      reason: `You have ${unplacedCreatures.length} unplaced creature${unplacedCreatures.length > 1 ? 's' : ''}. All creatures must be placed in habitats before you can explore.`

    };

  }



  // Check for unhappy creatures

  const unhappyCreatures: string[] = [];

  Object.values(habitatPlacements).forEach(habitatCreatures => {

    habitatCreatures.forEach(creature => {

      if (creature.happiness < 30) {

        const creatureData = creatures.find(c => c.id === creature.creature_id);

        if (creatureData) {

          unhappyCreatures.push(creatureData.name);

        }

      }

    });

  });



  if (unhappyCreatures.length > 0) {

    return {

      allowed: false,

      reason: `${unhappyCreatures.length} creature${unhappyCreatures.length > 1 ? 's are' : ' is'} unhappy: ${unhappyCreatures.join(', ')}. Rearrange your habitats or release creatures to improve compatibility before exploring.`

    };

  }



  return { allowed: true, reason: "" };

}